import {useI18n} from '@/i18n'
import {Input} from '@/components/ui/input'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {DatePicker} from '@/components/shared/DatePicker'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormField} from '@/components/shared/FormField'
import type {CreateCampaignRequest} from '@/types/certification'

/**
 * True when the campaign form satisfies the three-field submit constraint:
 * a non-empty name AND a due date AND an owner identity. Callers gate their
 * own submit button on this so the rule lives in one place (spec §5.3, §8.2).
 */
export function isCampaignFormValid(value: CreateCampaignRequest): boolean {
    return value.name.trim().length > 0
        && !!value.dueDate
        && !!value.ownerPartyId
}

export interface CampaignFormFieldsProps {
    value: CreateCampaignRequest
    onChange: (value: CreateCampaignRequest) => void
    ownerOptions: ComboboxOption[]
    onOwnerSearch: (query: string) => void
    ownerSearchLoading?: boolean
    selectedOwnerLabel?: string
    disabled?: boolean
}

/**
 * Shared campaign create fields (name / description / due date / owner) used by
 * the wizard Launch step's stage sub-form and the three standalone create
 * dialogs. Renders fields only — the submit button + dialog/step chrome stay
 * with the caller, gated via {@link isCampaignFormValid} (spec §3.5, §5.3).
 */
export function CampaignFormFields({
    value,
    onChange,
    ownerOptions,
    onOwnerSearch,
    ownerSearchLoading,
    selectedOwnerLabel,
    disabled = false,
}: CampaignFormFieldsProps) {
    const i18n = useI18n()

    return (
        <FormGrid>
            <FormField label={String(i18n.certificationCampaigns.name)} htmlFor="campaign-input-name">
                <Input
                    id="campaign-input-name"
                    value={value.name}
                    disabled={disabled}
                    onChange={(e) => onChange({...value, name: e.target.value})}
                    data-testid="campaign-input-name"
                />
            </FormField>
            <FormField label={String(i18n.certificationCampaigns.description)} htmlFor="campaign-input-description">
                <Input
                    id="campaign-input-description"
                    value={value.description ?? ''}
                    disabled={disabled}
                    onChange={(e) => onChange({...value, description: e.target.value || null})}
                    data-testid="campaign-input-description"
                />
            </FormField>
            <FormField label={String(i18n.certificationCampaigns.dueDate)} htmlFor="campaign-input-due-date">
                <DatePicker
                    testId="campaign-input-due-date"
                    value={value.dueDate}
                    disabled={disabled}
                    onChange={(iso) => onChange({...value, dueDate: iso})}
                    ariaLabel={String(i18n.certificationCampaigns.dueDate)}
                />
            </FormField>
            <FormField label={String(i18n.certificationCampaigns.ownerIdentity)} htmlFor="campaign-combobox-owner">
                <Combobox
                    options={ownerOptions}
                    value={value.ownerPartyId || null}
                    disabled={disabled}
                    onValueChange={(v) => onChange({...value, ownerPartyId: v ?? ''})}
                    onSearch={onOwnerSearch}
                    placeholder={i18n.certificationCampaigns.searchOwner}
                    emptyMessage={i18n.certificationCampaigns.noIdentitiesFound}
                    isLoading={ownerSearchLoading}
                    selectedLabel={selectedOwnerLabel}
                    data-testid="campaign-combobox-owner"
                />
            </FormField>
        </FormGrid>
    )
}
